import { useState, useRef, useEffect, useCallback } from "react"

// Global registry to track all popover controllers
const popoverRegistry = new Map<string, () => void>()

export const useCoordinatedPopover = (id: string) => {
  const [isOpen, setIsOpen] = useState(false)
  const toggleButtonRef = useRef<HTMLButtonElement>(null)

  // Function to close this specific popover
  const closePopover = useCallback(() => {
    setIsOpen(false)
  }, [])

  // Function to close all other popovers
  const closeOtherPopovers = useCallback(() => {
    popoverRegistry.forEach((closeFunction, registeredId) => {
      if (registeredId !== id) {
        closeFunction()
      }
    })
  }, [id])

  // Register this popover in the global registry
  useEffect(() => {
    popoverRegistry.set(id, closePopover)

    return () => {
      popoverRegistry.delete(id)
    }
  }, [id, closePopover])

  const openPopover = useCallback(() => {
    closeOtherPopovers()
    setIsOpen(true)
  }, [closeOtherPopovers])

  const togglePopover = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()

      if (isOpen) {
        closePopover()
      } else {
        openPopover()
      }
    },
    [isOpen, closePopover, openPopover]
  )

  const handleClickOutside = useCallback(
    (e: Event) => {
      // Don't close if clicking on our own toggle button
      if (toggleButtonRef.current && toggleButtonRef.current.contains(e.target as Node)) {
        return
      }

      // Check if clicking on any other popover toggle button
      const clickedElement = e.target as HTMLElement
      const isClickingAnotherToggle = clickedElement.closest("[data-popover-toggle]")

      if (isClickingAnotherToggle) {
        // Let the other popover handle opening, just close this one
        closePopover()
      } else {
        // Regular outside click, close this popover
        closePopover()
      }
    },
    [closePopover]
  )

  return {
    isOpen,
    togglePopover,
    closePopover,
    handleClickOutside,
    toggleButtonRef,
  }
}
